import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useHasMenu } from '../../../../auth/access';
import type { Chart } from '../../../dashboards/dashboards.api';
import { BarChart, ChartFrame } from '../../../dashboards/components/ChartCard';
import { myWorkApi } from '../../../tasks/tasks.api';
import type { MyPerformance } from '../../../tasks/tasks.types';
import useAsyncData from '../../hooks/useAsyncData';

const WEEKS = 8;
const DAY_MONTH = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });

/** "2026-10-05" → "5 Oct", read as a local date (no UTC shift). */
const weekLabel = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return DAY_MONTH.format(new Date(y, m - 1, d));
};

const hours = (v: number) => `${Number(Number(v).toFixed(1))}h`;

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 }}>{value}</Typography>
      {hint && (
        <Typography variant='caption' color='text.secondary'>
          {hint}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Home's performance graph: the signed-in employee's last eight weeks — hours planned against
 * hours logged, and tasks finished (on time when done by the due date). Needs My Work.
 */
export default function PerformanceWidget() {
  const allowed = useHasMenu('my.work');
  if (!allowed) return null;
  return <Performance />;
}

const EMPTY = null as MyPerformance | null;

function Performance() {
  const fetcher = useCallback(() => myWorkApi.performance(WEEKS), []);
  const { data, loading, error, refetch } = useAsyncData<MyPerformance | null>(fetcher, EMPTY, {
    code: 'MY_PERFORMANCE_FAILED',
    errorMessage: 'Could not load your performance',
    toastOnError: false,
  });

  const chart = useMemo<Chart | null>(
    () =>
      data && {
        key: 'my-performance',
        title: 'Hours per week',
        kind: 'BAR',
        unit: 'HOURS',
        link: null,
        series: [
          { name: 'Planned', points: data.weeks.map((w) => ({ label: weekLabel(w.weekStart), value: Number(w.plannedHours) })) },
          { name: 'Logged', points: data.weeks.map((w) => ({ label: weekLabel(w.weekStart), value: Number(w.loggedHours) })) },
        ],
      },
    [data]
  );

  const pct = data && Number(data.capacityHours) > 0 ? Math.round((Number(data.loggedHours) / Number(data.capacityHours)) * 100) : null;
  const action = (
    <Button component={RouterLink} to='/timesheet' size='small'>
      Timesheet
    </Button>
  );

  return (
    <ChartFrame title={`My performance · last ${WEEKS} weeks`} action={action}>
      {error ? (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      ) : loading || !data || !chart ? (
        <Stack gap={1}>
          <Skeleton variant='text' width='60%' height={40} />
          <Skeleton variant='rounded' height={180} />
        </Stack>
      ) : (
        <Stack gap={2}>
          <Stack direction='row' gap={3} flexWrap='wrap'>
            <Stat label='Hours logged' value={hours(data.loggedHours)} hint={`of ${hours(data.capacityHours)} available${pct !== null ? ` · ${pct}%` : ''}`} />
            <Stat label='Hours planned' value={hours(data.plannedHours)} />
            <Stat
              label='Tasks finished'
              value={String(data.tasksCompleted)}
              hint={data.tasksCompleted ? `${data.completedOnTime} on time` : undefined}
            />
          </Stack>
          {Number(data.loggedHours) === 0 && Number(data.plannedHours) === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No hours planned or logged in these weeks yet. Log your time on the Timesheet and it shows here.
            </Typography>
          ) : (
            <BarChart chart={chart} stacked={false} />
          )}
        </Stack>
      )}
    </ChartFrame>
  );
}
